import React, { createContext, useState, useContext, useEffect } from 'react';
import { superAdminAPI } from '../services/garageApi';
import { useAuth } from './AuthContext';

// Create the context
const SuperAdminContext = createContext();

// Create and export the hook
export const useSuperAdmin = () => {
  const context = useContext(SuperAdminContext);
  if (!context) {
    throw new Error('useSuperAdmin must be used within SuperAdminProvider');
  }
  return context;
};

// Create and export the provider
export const SuperAdminProvider = ({ children }) => {
  const { user } = useAuth();
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [allTenants, setAllTenants] = useState([]);
  const [systemStats, setSystemStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedTenant, setSelectedTenant] = useState(null);

  useEffect(() => {
    if (user?.super_admin) {
      setIsSuperAdmin(true);
      loadSuperAdminData();
    } else {
      setLoading(false);
    }
  }, [user]);

  const loadSuperAdminData = async () => {
    try {
      setLoading(true);
      // Load all tenants and system stats
      const [tenantsResponse, statsResponse] = await Promise.all([
        superAdminAPI.getAllTenants(),
        superAdminAPI.getSystemStats()
      ]);
      
      setAllTenants(tenantsResponse.data);
      setSystemStats(statsResponse.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading super admin data:', error);
      setLoading(false);
    }
  };

  const createTenant = async (tenantData) => {
    try {
      const response = await superAdminAPI.createTenant(tenantData);
      await loadSuperAdminData(); // Reload data
      return response;
    } catch (error) {
      throw error;
    }
  };

  const updateTenant = async (tenantId, data) => {
    try {
      const response = await superAdminAPI.updateTenant(tenantId, data);
      await loadSuperAdminData(); // Reload data
      return response;
    } catch (error) {
      throw error;
    }
  };

  const deleteTenant = async (tenantId) => {
    try {
      const response = await superAdminAPI.deleteTenant(tenantId);
      await loadSuperAdminData(); // Reload data
      return response;
    } catch (error) {
      throw error;
    }
  };

  const suspendTenant = async (tenantId, reason) => {
    try {
      const response = await superAdminAPI.suspendTenant(tenantId, { reason });
      await loadSuperAdminData(); // Reload data
      return response;
    } catch (error) {
      throw error;
    }
  };

  const activateTenant = async (tenantId) => {
    try {
      const response = await superAdminAPI.activateTenant(tenantId);
      await loadSuperAdminData(); // Reload data
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getTenantData = async (tenantId, dataType) => {
    try {
      const response = await superAdminAPI.getTenantData(tenantId, dataType);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const getTenantUsers = async (tenantId) => {
    try {
      const response = await superAdminAPI.getTenantUsers(tenantId);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const getTenantAnalytics = async (tenantId, period = 'monthly') => {
    try {
      const response = await superAdminAPI.getTenantAnalytics(tenantId, period);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const updateSystemSettings = async (settings) => {
    try {
      const response = await superAdminAPI.updateSystemSettings(settings);
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getSystemLogs = async (filters = {}) => {
    try {
      const response = await superAdminAPI.getSystemLogs(filters);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const backupTenantData = async (tenantId) => {
    try {
      const response = await superAdminAPI.backupTenantData(tenantId);
      return response;
    } catch (error) {
      throw error;
    }
  };

  const restoreTenantData = async (tenantId, backupId) => {
    try {
      const response = await superAdminAPI.restoreTenantData(tenantId, backupId);
      return response;
    } catch (error) {
      throw error;
    }
  };

  const value = {
    isSuperAdmin,
    allTenants,
    systemStats,
    loading,
    selectedTenant,
    setSelectedTenant,
    createTenant,
    updateTenant,
    deleteTenant,
    suspendTenant,
    activateTenant,
    getTenantData,
    getTenantUsers,
    getTenantAnalytics,
    updateSystemSettings,
    getSystemLogs,
    backupTenantData,
    restoreTenantData,
    reloadData: loadSuperAdminData
  };

  return (
    <SuperAdminContext.Provider value={value}>
      {children}
    </SuperAdminContext.Provider>
  );
};
